const KST_OFFSET_MIN = 9 * 60;
function toDate(value) {
    if (value instanceof Date)
        return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'number' && Number.isFinite(value))
        return new Date(value);
    if (typeof value !== 'string')
        return null;
    const raw = value.trim();
    if (raw === '')
        return null;
    // 'YYYY-MM-DD' 는 날짜 전용 값이다. Date 파서가 UTC 자정으로 읽으므로 KST 변환 시
    // 하루가 밀린다 — 타임존 변환 없이 그대로 쓰도록 정오 UTC 로 고정한다.
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw))
        return new Date(`${raw}T12:00:00Z`);
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
}
/** UTC 기준 Date 를 KST 벽시계로 옮긴 Date(게터가 KST 값을 돌려주도록). */
function toKstParts(date) {
    const shifted = new Date(date.getTime() + KST_OFFSET_MIN * 60_000);
    return {
        y: shifted.getUTCFullYear(),
        m: shifted.getUTCMonth() + 1,
        d: shifted.getUTCDate(),
        hh: shifted.getUTCHours(),
        mm: shifted.getUTCMinutes(),
    };
}
const pad2 = (n) => String(n).padStart(2, '0');
/**
 * unknown 을 표시용 문자열로. 평범한 객체는 `[object Object]` 가 아니라 빈 문자열로 떨군다
 * (Prisma Decimal 처럼 자체 toString 을 가진 값은 그 결과를 쓴다).
 */
export function stringifyDocValue(value) {
    if (typeof value === 'string')
        return value;
    if (typeof value === 'number' || typeof value === 'bigint')
        return String(value);
    if (typeof value === 'boolean')
        return value ? 'O' : 'X';
    if (value instanceof Date)
        return value.toISOString();
    if (value !== null && typeof value === 'object') {
        const own = value.toString;
        return typeof own === 'function' && own !== Object.prototype.toString
            ? String(value.toString())
            : '';
    }
    return '';
}
/** KST 'YYYY-MM-DD'. 파싱 불가면 null. */
export function formatKstDate(value) {
    const d = toDate(value);
    if (!d)
        return null;
    const p = toKstParts(d);
    return `${p.y}-${pad2(p.m)}-${pad2(p.d)}`;
}
/** KST 'YYYY-MM-DD HH:mm'. 파싱 불가면 null. */
export function formatKstDateTime(value) {
    const d = toDate(value);
    if (!d)
        return null;
    const p = toKstParts(d);
    return `${p.y}-${pad2(p.m)}-${pad2(p.d)} ${pad2(p.hh)}:${pad2(p.mm)}`;
}
/** 'HH:mm' 표기 (시각 전용 값 'HH:mm[:ss]' 도 그대로 통과). */
export function formatKstTime(value) {
    if (typeof value === 'string' && /^\d{2}:\d{2}/.test(value.trim())) {
        return value.trim().slice(0, 5);
    }
    const d = toDate(value);
    if (!d)
        return null;
    const p = toKstParts(d);
    return `${pad2(p.hh)}:${pad2(p.mm)}`;
}
function toNumber(value) {
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
        const raw = value.trim().replace(/,/g, '');
        if (raw === '')
            return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
    }
    // Prisma Decimal 등 toString 을 갖는 객체
    if (value !== null && typeof value === 'object') {
        const text = stringifyDocValue(value);
        if (text === '')
            return null;
        const n = Number(text);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}
function defaultDigits(format) {
    switch (format) {
        case 'MONEY':
            return 0;
        case 'QTY':
            return 4;
        case 'PERCENT':
            return 1;
        default:
            return 2;
    }
}
/** 천단위 구분 + 소수 자리. QTY 는 불필요한 뒤쪽 0 을 제거한다. */
function formatNumber(n, format, digits) {
    const max = digits ?? defaultDigits(format);
    const min = format === 'QTY' ? 0 : max;
    return n.toLocaleString('ko-KR', {
        minimumFractionDigits: Math.min(min, max),
        maximumFractionDigits: max,
    });
}
/**
 * 컨텍스트/행에서 dot 경로로 값을 읽는다. 중간이 없으면 undefined.
 * 배열 인덱스(`lines.0.itemName`)도 지원한다.
 */
export function resolveContextPath(source, path) {
    if (!path)
        return undefined;
    let cur = source;
    for (const seg of path.split('.')) {
        if (cur === null || cur === undefined)
            return undefined;
        if (Array.isArray(cur)) {
            const idx = Number(seg);
            if (!Number.isInteger(idx))
                return undefined;
            cur = cur[idx];
            continue;
        }
        if (typeof cur !== 'object')
            return undefined;
        cur = cur[seg];
    }
    return cur;
}
/** 바인딩 규칙에 따라 표시 문자열을 만든다. 값이 비면 `fallback`(기본 ''). */
export function formatDocumentValue(value, bind) {
    const fallback = bind?.fallback ?? '';
    if (value === null || value === undefined || value === '')
        return fallback;
    const format = bind?.format ?? 'TEXT';
    switch (format) {
        case 'DATE': {
            return formatKstDate(value) ?? fallback;
        }
        case 'DATETIME': {
            return formatKstDateTime(value) ?? fallback;
        }
        case 'NUMBER':
        case 'MONEY':
        case 'QTY': {
            const n = toNumber(value);
            return n === null ? fallback : formatNumber(n, format, bind?.digits);
        }
        case 'PERCENT': {
            const n = toNumber(value);
            return n === null ? fallback : `${formatNumber(n, 'PERCENT', bind?.digits)}%`;
        }
        case 'TEXT':
        default:
            return stringifyDocValue(value);
    }
}
/** BOUND 표의 합계행 — 지정 컬럼 키의 숫자 합. */
export function sumRowValues(rows, key) {
    let sum = 0;
    for (const row of rows) {
        const n = toNumber(row?.[key]);
        if (n !== null)
            sum += n;
    }
    return sum;
}
// -----------------------------------------------------------------------------
// 한글 금액 (거래명세서 「일금 ○○○원정」)
// -----------------------------------------------------------------------------
const KO_DIGITS = ['', '일', '이', '삼', '사', '오', '육', '칠', '팔', '구'];
const KO_SMALL_UNITS = ['', '십', '백', '천'];
const KO_BIG_UNITS = ['', '만', '억', '조', '경'];
/** 4자리 그룹 → 한글. 자릿수 1도 '일'을 붙인다(금액 위변조 방지 관행). */
function koreanGroup(group) {
    let out = '';
    const digits = String(group).padStart(4, '0').split('').map(Number);
    for (let i = 0; i < 4; i += 1) {
        const d = digits[i] ?? 0;
        if (d === 0)
            continue;
        out += `${KO_DIGITS[d]}${KO_SMALL_UNITS[3 - i]}`;
    }
    return out;
}
/**
 * 금액을 한글로 — 1234567 → '일백이십삼만사천오백육십칠'.
 * 음수·소수는 절사 후 처리하며, 0 은 '영'.
 */
export function toKoreanAmountWords(value) {
    const n = toNumber(value);
    if (n === null)
        return '';
    const abs = Math.floor(Math.abs(n));
    if (abs === 0)
        return '영';
    const groups = [];
    let rest = abs;
    while (rest > 0) {
        groups.push(rest % 10000);
        rest = Math.floor(rest / 10000);
    }
    if (groups.length > KO_BIG_UNITS.length)
        return String(abs);
    let out = '';
    for (let i = groups.length - 1; i >= 0; i -= 1) {
        const g = groups[i] ?? 0;
        if (g === 0)
            continue;
        out += `${koreanGroup(g)}${KO_BIG_UNITS[i]}`;
    }
    return `${n < 0 ? '마이너스' : ''}${out}`;
}
/** 거래명세서 관행 표기 — '일금 일백이십삼만원정'. */
export function toKoreanAmountSentence(value) {
    const words = toKoreanAmountWords(value);
    return words ? `일금 ${words}원정` : '';
}
//# sourceMappingURL=document-value.js.map